import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { KaneaLogo } from '../../components/Common/KaneaLogo';
import { API_URL } from '../../config';
import AuthBrandCanvas from '../../components/Auth/AuthBrandCanvas';
import usePageTitle from '../../hooks/usePageTitle';

// styles
import '../../styles/auth.css';

const ResetPasswordScreen: React.FC = () => {
  usePageTitle('Réinitialisation du mot de passe — KaneaBourse');
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (!token) {
      setError('Token de réinitialisation manquant ou invalide.');
    }
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (password !== confirmPassword) {
      setError('Les mots de passe ne correspondent pas.');
      return;
    }

    if (password.length < 8) {
      setError('Le mot de passe doit contenir au moins 8 caractères.');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/auth/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token: token,
          new_password: password,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Erreur lors de la réinitialisation.');
      }

      setSuccess(data.message || 'Votre mot de passe a été modifié avec succès !');

      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login');
      }, 3000);

    } catch (err: any) {
      setError(err.message || 'Une erreur est survenue.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-root">
      <div className="auth-panel">

        {/* ── Panneau gauche branding ── */}
        <div className="auth-brand-panel">
          <AuthBrandCanvas />
          <div className="auth-brand-spotlight" />

          <div className="auth-brand-logo">
            <div className="auth-brand-logo-img-wrap">
              <KaneaLogo variant="mark" title={null} />
            </div>
            <span className="auth-brand-name">KaneaBourse</span>
            <div className="auth-brand-logo-dot" />
          </div>

          <div className="auth-brand-body">
            <h2>
              N'investissez plus sur des <em>rumeurs</em>
            </h2>
            <p>
              KaneaBourse analyse froidement les bilans officiels BRVM —
              14 modèles de valorisation, indicateurs techniques, Nia votre copilote en français.
              Analysez ici, passez vos ordres chez votre SGI.
            </p>
            <div className="auth-brand-stats">
              <div>
                <div className="auth-brand-stat-value">10s</div>
                <div className="auth-brand-stat-label">Pour décider</div>
              </div>
              <div>
                <div className="auth-brand-stat-value">48</div>
                <div className="auth-brand-stat-label">Sociétés suivies</div>
              </div>
              <div>
                <div className="auth-brand-stat-value">24/7</div>
                <div className="auth-brand-stat-label">Copilote Nia</div>
              </div>
            </div>
          </div>

          <div className="auth-brand-footer">
            © {new Date().getFullYear()} KaneaBourse · Données EOD BRVM
          </div>
        </div>

        {/* ── Panneau droit formulaire ── */}
        <div className="auth-form-panel">
          <div className="auth-form-inner">

            {/* ── Logo Mobile ── */}
            <div className="auth-mobile-logo">
              <KaneaLogo variant="mark" title={null} />
              <span className="auth-mobile-logo-text">KaneaBourse</span>
            </div>

            {!token ? (
              <div>
                <div className="auth-form-header">
                  <h1>Lien invalide ❌</h1>
                  <p>Le lien de réinitialisation est invalide, expiré ou manquant.</p>
                </div>

                <div className="auth-error" style={{ marginTop: 20 }}>
                  <span>⚠</span> Token manquant ou expiré.
                </div>

                <Link to="/members/forgot-password" className="auth-btn-primary" style={{ display: 'block', textAlign: 'center', marginTop: 24 }}>
                  Demander un nouveau lien
                </Link>

                <div className="auth-form-link" style={{ marginTop: 20 }}>
                  <Link to="/login">← Retour à la connexion</Link>
                </div>
              </div>
            ) : (
              <div>
                <div className="auth-form-header">
                  <h1>Nouveau mot de passe 🔒</h1>
                  <p>Créez un nouveau mot de passe sécurisé pour votre compte.</p>
                </div>

                {success && (
                  <div className="auth-success" style={{
                    padding: '12px 16px',
                    backgroundColor: 'rgba(16, 185, 129, 0.1)',
                    border: '1px solid rgba(16, 185, 129, 0.2)',
                    borderRadius: '8px',
                    color: '#34d399',
                    fontSize: '0.875rem',
                    marginBottom: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <span style={{ fontWeight: 'bold' }}>✓</span> {success} (Redirection dans 3s...)
                  </div>
                )}

                {error && (
                  <div className="auth-error">
                    <span>⚠</span> {error}
                  </div>
                )}

                {!success && (
                  <form noValidate onSubmit={handleSubmit}>

                    <div className="auth-field">
                      <label htmlFor="password">Nouveau mot de passe</label>
                      <input
                        id="password"
                        type="password"
                        name="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Minimum 8 caractères"
                        autoComplete="new-password"
                      />
                    </div>

                    <div className="auth-field">
                      <label htmlFor="confirmPassword">Confirmer le mot de passe</label>
                      <input
                        id="confirmPassword"
                        type="password"
                        name="confirmPassword"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder="Confirmez le mot de passe"
                        autoComplete="new-password"
                      />
                    </div>

                    <button
                      type="submit"
                      className="auth-btn-primary"
                      disabled={loading}
                      style={{ marginTop: 8 }}
                    >
                      {loading ? 'Réinitialisation…' : 'Réinitialiser le mot de passe →'}
                    </button>

                    <div className="auth-form-link">
                      <Link to="/login">← Retour à la connexion</Link>
                    </div>

                  </form>
                )}
              </div>
            )}

          </div>
        </div>

      </div>
    </div>
  );
};

export default ResetPasswordScreen;
